import React, { useContext, useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Menu, LogOut, AlertTriangle, XCircle } from 'react-feather';
import { AuthContext } from '../../context/authContext';
import { getItem, setItem, STORAGE_KEYS } from '../../services/storage';
import { ToggleMiniSidebar, ToggleMobileSidebar } from '../../store/customizer/CustomizerSlice';
import { useLeerTituloCabecera } from '../../context/tituloCabeceraContext';
import EnvironmentIndicator from '../../components/EnvironmentIndicator';

const HeaderModern = () => {
  const { logoutGoogle } = useContext(AuthContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = React.useState(false);
  const dropdownRef = useRef(null);
  const tituloPagina = useLeerTituloCabecera();

  const userData = getItem(STORAGE_KEYS.USER);
  const user = userData?.profileObj;
  const adminUser = userData?.adminProfileObj ?? null;
  const { impersonating = false } = userData ?? {};
  const email = impersonating ? adminUser.email : user.email;
  const impersonateEmail = impersonating ? user.email : '';
  const impersonateName = impersonating ? (user.nombre || user.name || '') : '';
  const picture = (impersonating ? adminUser.picture : user.picture) ?? '/images/noUserPicture.jpg';
  const name = impersonating ? adminUser.name : user.name;

  // Cerrar dropdown con click fuera o ESC
  const closeMenu = useCallback(() => setShowProfileMenu(false), []);

  useEffect(() => {
    if (!showProfileMenu) return undefined;

    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) closeMenu();
    };
    const handleEsc = (e) => {
      if (e.key === 'Escape') closeMenu();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [showProfileMenu, closeMenu]);

  const logout = () => {
    logoutGoogle();
    navigate('/');
  };

  const handleEndImpersonation = () => {
    const currentUserSession = getItem(STORAGE_KEYS.USER) ?? {};
    const { adminProfileObj, tokenId } = currentUserSession;
    const newUserSession = {
      profileObj: adminProfileObj,
      tokenId,
      impersonating: false,
    };
    setItem(STORAGE_KEYS.USER, newUserSession);
    window.location.href = '/';
  };

  return (
    <header
      className="topbar"
      style={{
        backgroundColor: '#fff',
        borderBottom: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.08)',
        zIndex: 40,
      }}
    >
      <EnvironmentIndicator />

      {impersonating && (
        <div
          style={{
            width: '100%', backgroundColor: '#fef3c7', borderBottom: '2px solid #f59e0b',
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            padding: '8px 16px', gap: '12px',
          }}
        >
          <AlertTriangle size={18} style={{ color: '#92400e', flexShrink: 0 }} />
          <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#92400e' }}>
            Simulando usuario: {impersonateName || impersonateEmail}
          </span>
          <button
            type="button"
            onClick={handleEndImpersonation}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '4px',
              padding: '4px 12px', fontSize: '0.75rem', fontWeight: 700,
              borderRadius: '6px', border: 'none',
              backgroundColor: '#dc2626', color: '#fff',
              cursor: 'pointer', transition: 'all 0.15s',
            }}
          >
            <XCircle size={14} />
            Volver a mi sesión
          </button>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 24px',
          height: '56px',
        }}
      >
        {/* Left: Menu Toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            type="button"
            onClick={() => dispatch(ToggleMiniSidebar())}
            className="d-none d-lg-flex"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '50%',
              color: '#6b7280',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            <Menu size={20} />
          </button>
          <button
            type="button"
            onClick={() => dispatch(ToggleMobileSidebar())}
            className="d-lg-none"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '50%',
              color: '#6b7280',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Menu size={20} />
          </button>
          {tituloPagina && (
            <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '8px', minWidth: 0 }}>
              <h1 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#111827', margin: 0, lineHeight: 1.2 }}>
                {tituloPagina.texto}
              </h1>
              {tituloPagina.subtitulo && (
                <span style={{ fontSize: '0.72rem', color: '#9ca3af', lineHeight: 1.2 }}>
                  {tituloPagina.subtitulo}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Right: Profile */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Profile */}
          <div style={{ position: 'relative' }} ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '6px 8px',
                borderRadius: '8px',
                transition: 'background-color 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <img
                src={picture}
                alt={name}
                referrerPolicy="no-referrer"
                onError={(e) => { e.currentTarget.src = '/images/noUserPicture.jpg'; }}
                style={{
                  width: '34px',
                  height: '34px',
                  borderRadius: '50%',
                  border: '2px solid #e5e7eb',
                  objectFit: 'cover',
                }}
              />
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#111827', lineHeight: 1.3 }}>
                  {name}
                </span>
                <span style={{ fontSize: '0.7rem', color: '#9ca3af', lineHeight: 1.3 }}>{email}</span>
              </div>
            </button>

            {/* Profile Dropdown */}
            {showProfileMenu && (
              <div
                style={{
                  position: 'absolute',
                  right: 0,
                  top: '100%',
                  marginTop: '4px',
                  width: '220px',
                  backgroundColor: '#fff',
                  borderRadius: '12px',
                  boxShadow: '0 4px 24px rgb(0 0 0 / 0.12), 0 0 0 1px rgb(0 0 0 / 0.04)',
                  padding: '6px',
                  zIndex: 50,
                }}
              >
                {impersonating && (
                  <>
                    <div
                      style={{
                        padding: '8px 12px',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: '#d97706',
                        backgroundColor: '#fffbeb',
                        borderRadius: '8px',
                        marginBottom: '4px',
                      }}
                    >
                      Suplantando: {impersonateName || impersonateEmail}
                    </div>
                    <button
                      type="button"
                      onClick={handleEndImpersonation}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: '10px 12px',
                        fontSize: '0.8rem',
                        fontWeight: 500,
                        color: '#374151',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        borderRadius: '8px',
                        transition: 'background-color 0.15s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      Volver a sesión original
                    </button>
                    <div style={{ borderTop: '1px solid #f3f4f6', margin: '4px 0' }} />
                  </>
                )}

                <button
                  type="button"
                  onClick={logout}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '10px 12px',
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    color: '#dc2626',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    transition: 'background-color 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fef2f2'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <LogOut size={16} />
                  Cerrar sesión
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default HeaderModern;
